import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useAssessment } from "../context/AssessmentContext";
import { PageShell } from "../components/PageShell";
import { TopProgressBar } from "../components/TopProgressBar";
import { REVENUE_RANGES, EMPLOYEE_RANGES, ACCOUNTING_SOFTWARE, AUSTRALIAN_STATES } from "../data/questionnaire";

const Field = ({ label, children, required }) => (
  <label className="block">
    <div className="text-[11px] tracking-[0.22em] uppercase text-[#0A1A2F] font-medium mb-2">
      {label} {required && <span className="text-[#1FA8E0]">*</span>}
    </div>
    {children}
  </label>
);

const inputCls =
  "w-full bg-white border border-[#E5E7EB] rounded-sm px-4 py-3 text-[#0A1A2F] placeholder-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1FA8E0] focus:border-[#1FA8E0] transition-all";

export default function ClientInfo() {
  const navigate = useNavigate();
  const { client, updateClient } = useAssessment();

  const canContinue = client.business_name.trim() && client.industry.trim();

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canContinue) return;
    navigate("/questionnaire");
  };

  return (
    <>
      <PageShell progressBar={<TopProgressBar value={8} />} max="max-w-3xl">
        <div data-testid="client-info-page">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#1FA8E0] mb-4">
              Step 01 — Tell us about you
            </div>
            <h1 className="font-display font-bold text-4xl md:text-5xl text-[#0A1A2F] leading-tight tracking-tight">
              First, the essentials.
            </h1>
            <p className="mt-4 text-[#6B7280] leading-relaxed max-w-2xl">
              A few details so we can tailor the recommendations and prepare a scope of work
              that actually reflects your business. None of this is saved anywhere — it stays
              with you.
            </p>
          </motion.div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="mt-12 bg-white rounded-lg border border-[#E5E7EB] elevated-card p-8 md:p-10"
          >
            {/* Business stage toggle */}
            <div className="mb-8">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#0A1A2F] font-medium mb-3">
                Business stage <span className="text-[#1FA8E0]">*</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { value: "existing", label: "Existing business", sub: "Already trading" },
                  { value: "pre_start", label: "Haven't started yet", sub: "Pre-launch / planning" },
                ].map((opt) => {
                  const selected = client.stage === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`stage-${opt.value}`}
                      onClick={() => updateClient({ stage: opt.value })}
                      className={`text-left px-5 py-4 rounded-sm border transition-all duration-200 ${
                        selected
                          ? "border-[#0A1A2F] bg-[#0A1A2F] text-white"
                          : "border-[#E5E7EB] bg-white hover:border-[#1FA8E0] hover:bg-[#F5F7FA] text-[#0A1A2F]"
                      }`}
                    >
                      <div className="text-sm font-semibold">{opt.label}</div>
                      <div className={`text-xs mt-1 ${selected ? "text-white/70" : "text-[#6B7280]"}`}>{opt.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label={client.stage === "pre_start" ? "Working name (or idea)" : "Business name"} required>
                <input
                  data-testid="input-business-name"
                  type="text"
                  value={client.business_name}
                  onChange={(e) => updateClient({ business_name: e.target.value })}
                  placeholder={client.stage === "pre_start" ? "e.g. Working title for your idea" : "e.g. Westbrook Joinery Pty Ltd"}
                  className={inputCls}
                />
              </Field>

              <Field label="Industry" required>
                <input
                  data-testid="input-industry"
                  type="text"
                  value={client.industry}
                  onChange={(e) => updateClient({ industry: e.target.value })}
                  placeholder="e.g. Construction, Hospitality, eCommerce"
                  className={inputCls}
                />
              </Field>

              <Field label="Contact person">
                <input
                  data-testid="input-contact-person"
                  type="text"
                  value={client.contact_person}
                  onChange={(e) => updateClient({ contact_person: e.target.value })}
                  placeholder="Full name"
                  className={inputCls}
                />
              </Field>

              <Field label="State / location">
                <select
                  data-testid="select-state"
                  value={client.state_location}
                  onChange={(e) => updateClient({ state_location: e.target.value })}
                  className={inputCls}
                >
                  <option value="">Select location</option>
                  {AUSTRALIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>

              {client.stage === "existing" && (
                <>
                  <Field label="Annual revenue range">
                    <select
                      data-testid="select-revenue"
                      value={client.revenue_range}
                      onChange={(e) => updateClient({ revenue_range: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">Select range</option>
                      {REVENUE_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>

                  <Field label="Number of employees">
                    <select
                      data-testid="select-employees"
                      value={client.employees}
                      onChange={(e) => updateClient({ employees: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">Select range</option>
                      {EMPLOYEE_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>

                  <div className="md:col-span-2">
                    <Field label="Existing accounting software">
                      <select
                        data-testid="select-accounting-software"
                        value={client.accounting_software}
                        onChange={(e) => updateClient({ accounting_software: e.target.value })}
                        className={inputCls}
                      >
                        <option value="">Select software</option>
                        {ACCOUNTING_SOFTWARE.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                  </div>
                </>
              )}
            </div>

            <div className="mt-10 flex items-center justify-between pt-6 border-t border-[#E5E7EB]">
              <button
                type="button"
                data-testid="back-to-welcome-btn"
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 text-[#6B7280] hover:text-[#0A1A2F] transition-colors text-sm"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Back
              </button>

              <button
                type="submit"
                data-testid="continue-to-questionnaire-btn"
                disabled={!canContinue}
                className="inline-flex items-center gap-3 bg-[#0A1A2F] hover:bg-[#2F5D8C] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white px-8 py-3 rounded-sm transition-all duration-300"
              >
                <span className="text-xs tracking-[0.2em] uppercase">Begin Diagnostic</span>
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </motion.form>
        </div>
      </PageShell>
    </>
  );
}
